import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import { IFile } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../api/countries/get-countries-list'
import useTranslate from '../../../lib/hooks/use-translate'
import { languageEnumMapping } from '../../../lib/types/enums/languages/language.enum'
import { LanguageLevelEnum } from '../../../lib/types/enums/languages/language-level.enum'
import { staffPositionEnumMapping } from '../../../lib/types/enums/staff/staff-position.enum'
import { staffQualificationEnumMapping } from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { Staff } from '../../../lib/types/models/staff/staff'
import { getStaffDisplayName } from '../../../lib/utils/functions/staff.functions'
import StaffPersonalSpacePictureUpload from './staff-personal-space-picture-upload'

type Props = {
  staff: Staff
  onUpdateProfilePicture: (file: IFile) => void
  disabled?: boolean
}

const StaffPersonalSpaceBasicInformation = ({
  disabled,
  staff: {
    picture,
    firstName,
    lastName,
    maidenName,
    position,
    qualifications,
    languages,
    ...staff
  },
  onUpdateProfilePicture
}: Props) => {
  const { t, currentLanguage } = useTranslate()
  const languageMapping = languageEnumMapping()

  const getDisplayName = () =>
    getStaffDisplayName(
      firstName ? StringFormatter.capitalize(firstName) : undefined,
      lastName ? lastName.toUpperCase() : undefined,
      maidenName
    )

  const { data: countries } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  const practicedLanguages = languages.filter((l) => l.level !== LanguageLevelEnum.NOT_PRACTICED)

  return (
    <Box className={cn({ 'pointer-events-none': disabled })}>
      <InnerBox>
        <div className="flex gap-4 w-full items-center mobile:flex-col mobile:items-start">
          <div className="flex items-center gap-5">
            <StaffPersonalSpacePictureUpload
              picture={picture}
              onUpdateProfilePicture={onUpdateProfilePicture}
            />
            <Typography
              variant="text"
              color={tailwindColors.black.DEFAULT}
              className="mobile:flex hidden"
            >
              {getDisplayName()}
            </Typography>
          </div>

          <div className="w-full">
            <Typography
              variant="text"
              color={tailwindColors.black.DEFAULT}
              className="mobile:hidden"
            >
              {getDisplayName()}
            </Typography>
            <div className="w-full flex justify-between items-center  ">
              <div className="flex gap-2 mt-2 mobile:flex-col ">
                {position && (
                  <Badge variant={'white'} text={t(staffPositionEnumMapping[position])} />
                )}
                {qualifications && qualifications.length > 0 && (
                  <Badge
                    variant={'white'}
                    className="h-fit"
                    text={qualifications
                      .map((qualification) =>
                        t(staffQualificationEnumMapping[qualification].label)
                      )
                      .join(', ')}
                  />
                )}
                {practicedLanguages.length > 0 && (
                  <Badge
                    variant={'white'}
                    className="text-nowrap"
                    text={t('staff.personal.space.spoken.languages')}
                    icon={
                      <div className="flex gap-2">
                        {practicedLanguages.map((language) => {
                          const Icon = languageMapping[language.language]?.icon
                          return (
                            <div key={language.language}>
                              {<Icon className={'w-5 h-5 rounded-full'} />}
                            </div>
                          )
                        })}
                      </div>
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="h-4" />
        <Box className="bg-blue-light p-5">
          <div className="grid gap-11 grid-cols-5 row-span-2 mobile:grid-cols-1 mobile:gap-3">
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                {t('staff.personal.space.phone.number')}
              </Typography>
              <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                {staff.phoneNumber}
              </Typography>
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                Email
              </Typography>
              <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                {staff.user.email}
              </Typography>
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                {t('staff.personal.space.academic.zone')}
              </Typography>
              <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                {staff.academicZone}
              </Typography>
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                {t('staff.personal.space.nationality')}
              </Typography>
              <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                {countries?.data.find((c) => c.alpha2 === staff.nationality)?.translations[
                  currentLanguage
                ] || '-'}
              </Typography>
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                {t('staff.personal.space.social.security.number')}
              </Typography>
              <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                {staff.socialSecurityNumber ?? '-'}
              </Typography>
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                {t('staff.personal.space.birth.date')}
              </Typography>
              <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                {`${
                  staff.birthDate ? new Date(staff.birthDate).toLocaleDateString('fr-FR') : '-'
                }`}
              </Typography>
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                {t('staff.personal.space.birth.place')}
              </Typography>
              <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                {countries?.data.find((c) => c.countryCode === String(staff.birthPlace))
                  ?.translations[currentLanguage] || '-'}
              </Typography>
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                {t('staff.personal.space.birth.department')}
              </Typography>
              <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                {staff.birthDepartment ?? '-'}
              </Typography>
            </div>
            <div className="flex flex-col gap-1">
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                {t('staff.personal.space.birth.city')}
              </Typography>
              <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                {staff.birthCity ?? '-'}
              </Typography>
            </div>
          </div>
        </Box>
      </InnerBox>
    </Box>
  )
}

export default StaffPersonalSpaceBasicInformation
